import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { HiSparkles, HiMail, HiLocationMarker } from 'react-icons/hi';
import { FaGithub, FaInstagram, FaWhatsapp } from 'react-icons/fa';
import ScrollReveal from './ScrollReveal';

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  const contactInfo = [
    {
      icon: HiMail,
      label: 'Email',
      value: 'the360unity@gmail.com',
      href: 'mailto:the360unity@gmail.com',
      color: 'accent-green'
    },
    {
      icon: HiLocationMarker,
      label: 'Location',
      value: 'Durban, South Africa',
      color: 'accent-blue'
    }
  ];

  const socialLinks = [
    {
      icon: FaGithub,
      label: 'GitHub',
      href: 'https://github.com/kryptik-dev',
      color: 'accent-green'
    },
    {
      icon: FaInstagram,
      label: 'Instagram',
      href: 'https://www.instagram.com/kryptikk.dev/',
      color: 'accent-blue'
    },
    {
      icon: FaWhatsapp,
      label: 'WhatsApp',
      href: 'https://wa.me/27766934588',
      color: 'accent-green'
    }
  ];

  return (
    <section id="contact" ref={ref} className="py-32 relative overflow-hidden scroll-mt-20">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-80 h-80 opacity-5">
          <div className="w-full h-full bg-accent-green rounded-full animate-pulse-glow" style={{ filter: 'blur(80px)' }} />
        </div>
        <div className="absolute bottom-20 right-20 w-96 h-96 opacity-5">
          <div className="w-full h-full bg-accent-blue rounded-full animate-pulse-glow" style={{ filter: 'blur(100px)' }} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center space-x-2 glass-morphism px-6 py-3 rounded-full mb-6">
              <HiSparkles className="text-accent-green" />
              <span className="font-jetbrains text-accent-green tracking-wider text-sm">GET IN TOUCH</span>
            </div>
            <h2 className="text-display-2 font-space-grotesk font-bold mb-6">
              <span className="text-white">Let's Create </span>
              <span className="gradient-text-green">Something </span>
              <span className="metallic-text">Amazing</span>
            </h2>
            <div className="max-w-5xl mx-auto">
              <ScrollReveal
                baseOpacity={0}
                enableBlur={true}
                baseRotation={3}
                blurStrength={8}
                containerClassName="text-center"
                textClassName="text-xl text-gray-300 leading-relaxed"
              >
                Ready to bring your vision to life? I'm always excited to discuss new projects, 
                innovative ideas, and opportunities to create exceptional digital experiences together.
              </ScrollReveal>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <motion.div variants={itemVariants} className="space-y-8">
              {/* Contact Info */}
              <div className="glass-morphism-strong rounded-2xl p-8">
                <h3 className="text-2xl font-space-grotesk font-bold gradient-text-blue mb-8">
                  Get In Touch
                </h3>
                <div className="space-y-6">
                  {contactInfo.map((item, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ x: 10 }}
                      className="flex items-center space-x-4 p-4 rounded-lg hover:bg-glass-light transition-colors cursor-pointer"
                    >
                      <div className={`p-3 bg-${item.color}/20 rounded-xl`}>
                        <item.icon className={`text-xl text-${item.color}`} />
                      </div>
                      <div>
                        <p className="text-sm text-gray-300">{item.label}</p>
                        {item.href ? (
                          <a
                            href={item.href}
                            className="text-white hover:text-accent-green transition-colors"
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-white">{item.value}</p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Social Links */}
              <div className="glass-morphism-strong rounded-2xl p-8">
                <h3 className="text-2xl font-space-grotesk font-bold metallic-text mb-8">
                  Connect Online
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ y: -5, scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`p-6 glass-morphism rounded-xl text-center group hover:bg-${social.color}/10 transition-colors`}
                    >
                      <social.icon className={`text-3xl text-${social.color} mx-auto mb-3 group-hover:scale-110 transition-transform`} />
                      <p className="text-sm text-gray-300">{social.label}</p>
                    </motion.a>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div className="glass-morphism-strong rounded-2xl p-8">
                <h3 className="text-2xl font-space-grotesk font-bold gradient-text-green mb-6">
                  Availability
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-accent-green rounded-full animate-pulse"></div>
                    <span className="text-white font-medium">Open for new projects</span>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    I'm currently accepting new clients and excited to work on innovative projects. 
                    Typical response time is within 24 hours.
                  </p>
                  <div className="pt-4 border-t border-glass-medium">
                    <p className="text-sm text-gray-400">
                      <strong>Best time to reach me:</strong> Monday - Friday, 9 AM - 6 PM (SAST)
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Additional Contact Info */}
            <motion.div variants={itemVariants}>
              <div className="glass-morphism-strong rounded-2xl p-8 h-full">
                <h3 className="text-2xl font-space-grotesk font-bold gradient-text-green mb-8">
                  Let's Connect
                </h3>
                <div className="space-y-6">
                  <div className="text-gray-300 leading-relaxed">
                    <p className="mb-4">
                      I'm passionate about creating innovative solutions and always excited to collaborate on new projects. 
                      Whether you have a specific project in mind or just want to explore possibilities, I'd love to hear from you.
                    </p>
                    <p className="mb-4">
                      Feel free to reach out via email or connect with me on social media. I'm always open to discussing:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>New project opportunities</li>
                      <li>Technical consulting</li>
                      <li>Collaboration possibilities</li>
                      <li>Innovation discussions</li>
                    </ul>
                  </div>
                  
                  <div className="pt-6 border-t border-glass-medium">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-2 h-2 bg-accent-green rounded-full"></div>
                      <span className="text-white font-medium">Quick Response</span>
                    </div>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-2 h-2 bg-accent-blue rounded-full"></div>
                      <span className="text-white font-medium">Flexible Communication</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-accent-green rounded-full"></div>
                      <span className="text-white font-medium">Professional Approach</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;